import { useState } from 'react';
import _ from 'lodash';
import { TextField, Box, Flex, IconButton, Button, Icon, Text, Divider, List, ListItem, Tooltip } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Dropdown } from '@vibe/core';
import { Heading } from '@vibe/core';
import { Workflow as WorkflowIcon, Alert as AlertIcon, AddSmall as AddSmallIcon } from '@vibe/icons';
import { type Board } from '@gorilla/common/src/lib/monday-api/api';
import { ExternalLink } from './ExternalLink';

const MATCHING_OPTIONS = [
  {
    value: 'and',
    label: 'Match all',
    endElement: {
      type: 'suffix',
      value: 'AND',
    },
  },
  {
    value: 'or',
    label: 'Match any',
    endElement: {
      type: 'suffix',
      value: 'OR',
    },
  },
] as const;

type MatchingOperator = (typeof MATCHING_OPTIONS)[number]['value'];

function getMatchingOption(value: MatchingOperator) {
  return MATCHING_OPTIONS.find((option) => option.value === value) || null;
}

function getConditionPrefix(index: number, operator: MatchingOperator) {
  if (index === 0) return 'Show if';

  const selectedOption = getMatchingOption(operator);

  switch (selectedOption?.value) {
    case 'or':
      return 'or if';
    case 'and':
    default:
      return 'and if';
  }
}

type StatusFieldCondition = {
  type: 'status';
  column_id: string | null;
  operator: 'equal' | 'not_equal' | 'is_empty' | 'is_not_empty';
  value: string | null;
};

type Condition = StatusFieldCondition;

export function validateStatusCondition(board: Board, condition: StatusFieldCondition) {
  const column = board.columns.find((column) => column.id === condition.column_id);

  if (!column) {
    return 'Unknown column';
  }

  if (column.type !== 'status') {
    return 'Column is not a status column';
  }

  return true;
}

export function areConditionsValid(board: Board, conditions: Condition[]) {
  return conditions.every((condition) => {
    switch (condition.type) {
      case 'status':
        return validateStatusCondition(board, condition) === true;
      default:
        return false;
    }
  });
}

type StatusFieldConditionCompProps = {
  board: Board;
  condition: StatusFieldCondition;
  onChange: (newCondition: StatusFieldCondition) => void;
  index: number;
  operator: MatchingOperator;
};

function StatusFieldConditionComp({ board, condition, onChange, index, operator }: StatusFieldConditionCompProps) {
  const statusColumns = board.columns.filter((column) => column.type === 'status');

  const statusColumnOptions = statusColumns.map((column) => ({
    value: column.id,
    label: column.title,
  }));

  const selectedColumnOption = statusColumnOptions.find((option) => option.value === condition.column_id) || null;

  const operatorOptions = [
    { value: 'equal', label: 'equal' },
    { value: 'not_equal', label: 'not equal' },
    { value: 'is_empty', label: 'empty' },
    { value: 'is_not_empty', label: 'not empty' },
  ];

  const selectedOperatorOption = operatorOptions.find((option) => option.value === condition.operator) || null;
  const validationMessage = validateStatusCondition(board, condition);

  return (
    <Flex gap={10} style={{ width: '100%' }}>
      {_.isString(validationMessage) ? (
        <Tooltip content={validationMessage} position="right" zIndex={10000}>
          <Box style={{ color: '#d83a52' }}>
            <Icon icon={AlertIcon} size={18} style={{ transform: 'translate3d(0,2px,0)' }} />
          </Box>
        </Tooltip>
      ) : null}

      <Text type="text2" align="start" color="primary" element="p" style={{ minWidth: '55px', maxWidth: '55px' }}>
        {getConditionPrefix(index, operator)}
      </Text>

      <Box style={{ width: '50%' }}>
        <Dropdown
          options={statusColumnOptions}
          value={dropdownValue(selectedColumnOption)}
          placeholder="Select column"
          clearable={false}
          size="small"
          onChange={(newField: any) => {
            onChange({ ...condition, column_id: newField.value });
          }}
        />
      </Box>

      <Text type="text2" align="center" color="primary" element="p" style={{ minWidth: '25px', maxWidth: '25px' }}>
        is
      </Text>

      <Box style={{ width: '50%' }}>
        <Dropdown
          options={operatorOptions}
          value={dropdownValue(selectedOperatorOption)}
          placeholder="Select operator"
          clearable={false}
          size="small"
          onChange={(newOperator: any) => {
            onChange({ ...condition, operator: newOperator.value });
          }}
        />
      </Box>

      {selectedColumnOption && (selectedOperatorOption?.value === 'equal' || selectedOperatorOption?.value === 'not_equal') ? (
        <>
          <Text type="text2" align="center" color="primary" element="p" style={{ minWidth: '25px', maxWidth: '25px' }}>
            to
          </Text>
          <Box style={{ width: '50%' }}>
            <TextField
              placeholder="e.g. Done"
              value={condition.value || ''}
              size="small"
              onChange={(newValue: any) => {
                onChange({ ...condition, value: newValue });
              }}
              autoFocus
            />
          </Box>
        </>
      ) : null}
    </Flex>
  );
}

const COLUMN_TYPE_TO_CONDITION = {
  status: StatusFieldConditionComp,
};

type ConditionsModalProps = {
  board: Board;
  conditions: Condition[];
  conditionsOperator?: MatchingOperator;
  isOpen: boolean;
  onClose: (conditions: Condition[] | null, conditionsOperator?: MatchingOperator) => void;
  isButtonCondition: boolean;
};

export function ConditionsModal({ isOpen, onClose, board, conditions, conditionsOperator, isButtonCondition }: ConditionsModalProps) {
  const [conditionsDraft, setConditionsDraft] = useState(() => (conditions ? _.cloneDeep(conditions) : []));
  const [operatorDraft, setOperatorDraft] = useState<MatchingOperator>(conditionsOperator || 'and');
  const firstStatusColumn = board.columns.find((column) => column.type === 'status');
  const selectedMatchingOption = getMatchingOption(operatorDraft);

  return (
    <AppModal
      show={isOpen}
      onClose={() => {
        onClose(null);
      }}
      contentSpacing={false}
      alertDialog={true}
      width={800}
      id="conditions__modal"
    >
      <AppModalContent>
        <div>
          <Box>
            <Flex direction="row" align="center" justify="space-between" gap={8}>
              <Flex direction="row" align="center" gap={8}>
                <Icon icon={WorkflowIcon} size={24} />
                <Heading type="h3" weight="bold" align="start">
                  Conditions
                </Heading>
              </Flex>
            </Flex>

            <Box
              rounded="small"
              border={true}
              borderColor="uiBorderColor"
              style={{
                margin: '24px 0',
                backgroundColor: 'rgba(103, 104, 121, 0.025)',
              }}
            >
              {!conditionsDraft.length ? (
                <Flex
                  direction="column"
                  justify="center"
                  align="center"
                  gap={8}
                  style={{
                    margin: '0 auto',
                    width: '350px',
                    height: '258px',
                  }}
                >
                  <Icon icon={WorkflowIcon} size={40} />
                  <Heading type="h3" weight="bold" align="center">
                    No conditions
                  </Heading>
                  <Text type="text2" align="center" color="secondary" element="p">
                    Use conditions to display&nbsp;
                    <ExternalLink
                      href="https://getgorilla.app/products/client-portal/docs/user-guides/action-buttons"
                      target="_blank"
                      style={{ fontSize: '14px', textDecoration: 'underline', color: '#676879', whiteSpace: 'nowrap' }}
                    >
                      action buttons
                    </ExternalLink>
                    ,&nbsp;or&nbsp;
                    <ExternalLink
                      href="https://getgorilla.app/products/client-portal/docs/user-guides/conditional-content"
                      target="_blank"
                      style={{ fontSize: '14px', textDecoration: 'underline', color: '#676879', whiteSpace: 'nowrap' }}
                    >
                      pages
                      <br /> and sections
                    </ExternalLink>
                    &nbsp;only under certain circumstances.
                  </Text>
                </Flex>
              ) : (
                <Box
                  style={{
                    overflowY: 'auto',
                    maxHeight: '258px',
                  }}
                >
                  <List
                    style={{
                      overflow: 'hidden',
                    }}
                  >
                    {conditionsDraft.map((condition, index) => {
                      const ConditionComponent = COLUMN_TYPE_TO_CONDITION[condition.type];

                      return (
                        <>
                          <Box key={condition.column_id}>
                            <ListItem size="medium" className="portalmanager__content__listitem--noclick">
                              <Flex gap={10} style={{ width: '100%' }}>
                                <ConditionComponent
                                  index={index}
                                  key={index}
                                  board={board}
                                  condition={condition}
                                  operator={operatorDraft}
                                  onChange={(newCondition) => {
                                    setConditionsDraft((draft) => {
                                      const newDraft = _.cloneDeep(draft);
                                      newDraft[index] = newCondition;
                                      return newDraft;
                                    });
                                  }}
                                />
                                <Button
                                  kind="tertiary"
                                  size="small"
                                  onClick={() => {
                                    setConditionsDraft((draft) => {
                                      const newDraft = _.cloneDeep(draft);
                                      newDraft.splice(index, 1);
                                      return newDraft;
                                    });
                                  }}
                                  style={{
                                    marginLeft: '5px',
                                  }}
                                >
                                  Delete
                                </Button>
                              </Flex>
                            </ListItem>
                          </Box>
                          {index !== conditionsDraft.length - 1 && <Divider withoutMargin />}
                        </>
                      );
                    })}
                  </List>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            style={{
              marginBottom: '10px',
            }}
          >
            <Flex gap={16}>
              <Box
                style={{
                  flex: 1,
                }}
              >
                <Flex gap={24}>
                  {conditionsDraft.length >= 2 ? (
                    <>
                      <IconButton
                        kind={!conditionsDraft.length ? 'primary' : 'secondary'}
                        size="medium"
                        icon={AddSmallIcon}
                        disabled={!firstStatusColumn}
                        onClick={() => {
                          if (firstStatusColumn) {
                            setConditionsDraft([
                              ...conditionsDraft,
                              { type: 'status', column_id: firstStatusColumn.id, operator: 'equal', value: '' },
                            ]);
                          }
                        }}
                        tooltipContent="Add condition"
                        tooltipProps={{ position: 'right', showOnDialogEnter: false }}
                      />
                      <div style={{ width: '180px' }}>
                        <Dropdown
                          options={MATCHING_OPTIONS as any}
                          value={dropdownValue(selectedMatchingOption)}
                          onChange={(selectedOption: any) => {
                            setOperatorDraft(selectedOption.value as MatchingOperator);
                          }}
                          clearable={false}
                          searchable={false}
                        />
                      </div>
                    </>
                  ) : (
                    <Button
                      kind={!conditionsDraft.length ? 'primary' : 'secondary'}
                      size="medium"
                      leftIcon={AddSmallIcon}
                      disabled={!firstStatusColumn}
                      onClick={() => {
                        if (firstStatusColumn) {
                          setConditionsDraft([
                            ...conditionsDraft,
                            { type: 'status', column_id: firstStatusColumn.id, operator: 'equal', value: '' },
                          ]);
                        }
                      }}
                    >
                      Add condition
                    </Button>
                  )}

                  <Text type="text2" align="start" color="primary" ellipsis element="p">
                    Support is limited to status columns.
                    <ExternalLink
                      href={
                        isButtonCondition
                          ? 'https://getgorilla.app/products/client-portal/docs/user-guides/action-buttons'
                          : 'https://getgorilla.app/products/client-portal/docs/user-guides/conditional-content'
                      }
                      target="_blank"
                      style={{ marginLeft: '8px', fontSize: '14px', textDecoration: 'underline', color: '#323338', whiteSpace: 'nowrap' }}
                    >
                      Learn more
                    </ExternalLink>
                  </Text>
                </Flex>
              </Box>

              <Box marginEnd="auto">
                <Flex gap={10}>
                  <Button
                    kind="tertiary"
                    size="medium"
                    onClick={() => {
                      onClose(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    kind={!conditionsDraft.length ? 'secondary' : 'primary'}
                    size="medium"
                    disabled={!areConditionsValid(board, conditionsDraft)}
                    onClick={() => {
                      onClose(conditionsDraft, operatorDraft);
                    }}
                  >
                    Save
                  </Button>
                </Flex>
              </Box>
            </Flex>
          </Box>
        </div>
      </AppModalContent>
    </AppModal>
  );
}
